import { addressCount, lastAddress, networkAddress } from './ipv4'
import type { Cidr } from './types'

/**
 * Cloud providers reserve addresses inside every subnet, so the usable host count
 * differs from plain IPv4 arithmetic. Sources:
 * - AWS:   https://docs.aws.amazon.com/vpc/latest/userguide/subnet-sizing.html
 * - Azure: https://learn.microsoft.com/en-us/azure/virtual-network/virtual-networks-faq
 * - GCP:   https://docs.cloud.google.com/vpc/docs/subnets
 * - OCI:   https://docs.oracle.com/en-us/iaas/Content/Network/Concepts/overview.htm
 */
export type CloudMode = 'standard' | 'aws' | 'azure' | 'gcp' | 'oci'

export const CLOUD_MODES: readonly CloudMode[] = ['standard', 'aws', 'azure', 'gcp', 'oci']

export interface ReservedOffset {
  readonly offset: number
  readonly reasonKey: string
}

export interface CloudProfile {
  readonly mode: CloudMode
  /** i18n key for the mode's display name. */
  readonly labelKey: string
  /** Longest prefix (smallest subnet) the provider permits. */
  readonly smallestPrefix: number
  /** Official documentation stating these reservations. Cite, never paraphrase. */
  readonly docsUrl: string
  /** Human-readable name of that document, shown as the link text. */
  readonly docsLabel: string
  /** Offsets counted forward from the network address. */
  readonly reservedOffsets: readonly ReservedOffset[]
  /**
   * Offsets counted backward from the last address: 0 is the last address itself,
   * 1 the second-to-last, and so on.
   *
   * This exists because Google Cloud reserves the second-to-last address — every
   * other provider only takes a run at the start plus the final address, so a
   * boolean used to be enough.
   */
  readonly reservedFromEnd: readonly ReservedOffset[]
}

const NETWORK = { offset: 0, reasonKey: 'reserved.network' } as const
const BROADCAST = [{ offset: 0, reasonKey: 'reserved.broadcast' }] as const

export const CLOUD_PROFILES: Readonly<Record<CloudMode, CloudProfile>> = {
  standard: {
    mode: 'standard',
    labelKey: 'cloud.standard',
    docsUrl: 'https://www.rfc-editor.org/rfc/rfc1812#section-4.2.3.1',
    docsLabel: 'RFC 1812 § 4.2.3.1',
    smallestPrefix: 32,
    reservedOffsets: [NETWORK],
    reservedFromEnd: BROADCAST,
  },
  aws: {
    mode: 'aws',
    labelKey: 'cloud.aws',
    docsUrl: 'https://docs.aws.amazon.com/vpc/latest/userguide/subnet-sizing.html',
    docsLabel: 'AWS — Subnet sizing for IPv4',
    smallestPrefix: 28,
    reservedOffsets: [
      NETWORK,
      { offset: 1, reasonKey: 'reserved.awsRouter' },
      { offset: 2, reasonKey: 'reserved.awsDns' },
      { offset: 3, reasonKey: 'reserved.awsFuture' },
    ],
    reservedFromEnd: BROADCAST,
  },
  azure: {
    mode: 'azure',
    labelKey: 'cloud.azure',
    docsUrl:
      'https://learn.microsoft.com/en-us/azure/virtual-network/virtual-networks-faq#are-there-any-restrictions-on-using-ip-addresses-within-these-subnets',
    docsLabel: 'Azure — Virtual network FAQ',
    smallestPrefix: 29,
    reservedOffsets: [
      NETWORK,
      { offset: 1, reasonKey: 'reserved.azureGateway' },
      { offset: 2, reasonKey: 'reserved.azureDns' },
      { offset: 3, reasonKey: 'reserved.azureDns' },
    ],
    reservedFromEnd: BROADCAST,
  },

  gcp: {
    mode: 'gcp',
    labelKey: 'cloud.gcp',
    docsUrl: 'https://docs.cloud.google.com/vpc/docs/subnets#unusable-ip-addresses-in-every-subnet',
    docsLabel: 'Google Cloud — VPC subnets',
    smallestPrefix: 29,
    reservedOffsets: [NETWORK, { offset: 1, reasonKey: 'reserved.gcpGateway' }],
    // The second-to-last address is "reserved by Google Cloud for potential future
    // use" — the only mid-subnet reservation among the providers we support.
    reservedFromEnd: [
      { offset: 0, reasonKey: 'reserved.broadcast' },
      { offset: 1, reasonKey: 'reserved.gcpFuture' },
    ],
  },
  oci: {
    mode: 'oci',
    labelKey: 'cloud.oci',
    docsUrl:
      'https://docs.oracle.com/en-us/iaas/Content/Network/Concepts/overview.htm#Reserved__reserved_subnet',
    docsLabel: 'Oracle Cloud — Networking overview',
    smallestPrefix: 30,
    reservedOffsets: [NETWORK, { offset: 1, reasonKey: 'reserved.ociGateway' }],
    reservedFromEnd: BROADCAST,
  },
}

export interface ReservedAddress {
  readonly address: number
  /** i18n key explaining why the address is unavailable. */
  readonly reasonKey: string
}

/**
 * Addresses inside `cidr` that cannot be assigned to a host.
 *
 * In standard mode a /31 is a point-to-point link with two usable addresses and no
 * broadcast address (RFC 3021), and a /32 is a single host route — neither reserves
 * anything. Cloud modes never reach those sizes because their minimums are larger.
 */
export function reservedAddresses(cidr: Cidr, mode: CloudMode = 'standard'): ReservedAddress[] {
  const profile = CLOUD_PROFILES[mode]
  const network = networkAddress(cidr)
  const last = lastAddress(cidr)
  const total = addressCount(cidr.prefix)

  if (mode === 'standard' && cidr.prefix >= 31) return []

  // Keyed by address so the two ends cannot double-count: in a subnet small enough
  // for the leading and trailing reservations to overlap, an address is reserved
  // once, for the first reason that claims it.
  const byAddress = new Map<number, string>()
  const claim = (address: number, reasonKey: string): void => {
    if (!byAddress.has(address)) byAddress.set(address, reasonKey)
  }

  for (const { offset, reasonKey } of profile.reservedOffsets) {
    if (offset < total) claim(network + offset, reasonKey)
  }
  for (const { offset, reasonKey } of profile.reservedFromEnd) {
    if (offset < total) claim(last - offset, reasonKey)
  }

  return [...byAddress.entries()]
    .sort(([a], [b]) => a - b)
    .map(([address, reasonKey]) => ({ address, reasonKey }))
}

/** Addresses in `cidr` that can be assigned to a host. Never negative. */
export function usableHostCount(cidr: Cidr, mode: CloudMode = 'standard'): number {
  return Math.max(0, addressCount(cidr.prefix) - reservedAddresses(cidr, mode).length)
}

/**
 * First and last assignable address, or `null` when the subnet has no usable range.
 *
 * Walks inward from both ends, which assumes reservations sit at the edges — true
 * for all five providers, including Google Cloud's second-to-last address. A
 * provider reserving something mid-range would need this to return several ranges.
 */
export function usableRange(
  cidr: Cidr,
  mode: CloudMode = 'standard',
): { first: number; last: number } | null {
  const reserved = new Set(reservedAddresses(cidr, mode).map((entry) => entry.address))
  const network = networkAddress(cidr)
  const last = lastAddress(cidr)
  let first = network
  while (first <= last && reserved.has(first)) first += 1
  let end = last
  while (end >= first && reserved.has(end)) end -= 1
  return first <= end ? { first, last: end } : null
}

/** True when the provider permits a subnet of this size. */
export function isAllowedSize(cidr: Cidr, mode: CloudMode = 'standard'): boolean {
  return cidr.prefix <= CLOUD_PROFILES[mode].smallestPrefix
}
